import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useLocation } from 'react-router-dom';
import { Settings2, CheckSquare, Square } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { APIError } from '@/lib/api/studysnap';
import { OutputDisplay } from '@/components/output/output-display';
import { useAuth } from '@/lib/auth/context';
import { AuthDialog } from '@/components/auth/auth-dialog';
import { YouTubeService } from '@/lib/services/youtube';
import { FlashcardSet } from '@/lib/types/flashcard';

interface GenerateOptions {
  generate_mcq: boolean;
  generate_glossary: boolean;
  generate_book: boolean;
  generate_flashcards: boolean;
}

const steps = ["Analyzing", "Extracting", "Generating", "Finalizing"];

export function Launch() {
  const location = useLocation();
  const { user } = useAuth();
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [isValidUrl, setIsValidUrl] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [generatedData, setGeneratedData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const [options, setOptions] = useState<GenerateOptions>({
    generate_mcq: true,
    generate_glossary: true,
    generate_book: true,
    generate_flashcards: true,
  });
  const [flashcardData, setFlashcardData] = useState<FlashcardSet | null>(null);

  useEffect(() => {
    // Auto-fill URL from navigation state if available
    const state = location.state as { youtubeUrl?: string };
    if (state?.youtubeUrl) {
      setYoutubeUrl(state.youtubeUrl);
      setIsValidUrl(validateYoutubeUrl(state.youtubeUrl));
    }
  }, [location.state]);

  const validateYoutubeUrl = (url: string) => {
    const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+$/;
    return youtubeRegex.test(url);
  };

  const handleSubmit = async () => {
    if (!user) {
      setIsAuthOpen(true);
      return;
    }

    setIsLoading(true);
    setError(null);
    setGeneratedData(null);
    setFlashcardData(null);

    try {
      // Process video and get transcript
      const videoData = await YouTubeService.processVideo(youtubeUrl, {
        generateMCQ: options.generate_mcq,
        generateGlossary: options.generate_glossary,
        generateBook: options.generate_book,
        generateFlashcards: options.generate_flashcards,
      });

      // Set generated data
      setGeneratedData({
        mcqData: videoData.mcq,
        glossaryData: videoData.glossary,
        bookData: videoData.book,
      });

      // Set flashcard data if available
      if (videoData.flashcards) {
        setFlashcardData(videoData.flashcards);
      }

      toast.success('Study materials generated successfully!');
    } catch (error) {
      console.error('Error:', error);
      setError(
        error instanceof APIError
          ? error.message
          : 'An error occurred while processing the video'
      );
      toast.error('Failed to generate study materials');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <div className="container mx-auto px-4 py-8">
        {!user && (
          <div className="mb-8 rounded-lg bg-white p-6 shadow-sm">
            <h2 className="text-lg font-medium">Sign in to Generate Study Material</h2>
            <p className="mt-2 text-gray-600">
              Please sign in to access the full functionality of StudySnap.
            </p>
            <Button onClick={() => setIsAuthOpen(true)} className="mt-4">
              Sign In
            </Button>
          </div>
        )}

        <div className="space-y-8">
          <div>
            <h1 className="text-2xl font-bold">Generate Study Material</h1>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700">
                YouTube URL
              </label>
              <input
                type="text"
                value={youtubeUrl}
                onChange={(e) => {
                  setYoutubeUrl(e.target.value);
                  setIsValidUrl(validateYoutubeUrl(e.target.value));
                }}
                placeholder="Enter YouTube video URL"
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </div>
          </div>

          <div className="rounded-lg bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-medium">Options</h2>
              <Settings2 className="h-5 w-5 text-gray-400" />
            </div>
            <div className="mt-4 space-y-2">
              {Object.entries(options).map(([key, value]) => (
                <label key={key} className="flex items-center gap-2">
                  <button
                    onClick={() =>
                      setOptions((prev) => ({
                        ...prev,
                        [key]: !value,
                      }))
                    }
                    className="rounded p-0.5 hover:bg-gray-100"
                  >
                    {value ? (
                      <CheckSquare className="h-5 w-5 text-indigo-600" />
                    ) : (
                      <Square className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                  <span className="text-sm">
                    {key === 'generate_book' 
                      ? 'Generate Smart Notes'
                      : key
                          .split('_')
                          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                          .join(' ')}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={handleSubmit}
              disabled={!isValidUrl || isLoading}
              className="min-w-[120px]"
            >
              {isLoading ? 'Processing...' : 'Generate'}
            </Button>
          </div>

          {error && (
            <div className="rounded-lg bg-red-50 p-4 text-red-700">{error}</div>
          )}

          {(generatedData || flashcardData) && (
            <OutputDisplay 
              mcqData={generatedData?.mcqData} 
              glossaryData={generatedData?.glossaryData} 
              bookData={generatedData?.bookData}
              flashcardData={flashcardData || undefined}
            />
          )}
        </div>
      </div>

      <AuthDialog isOpen={isAuthOpen} onClose={() => setIsAuthOpen(false)} />
    </div>
  );
}